import { useCallback, useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Row,
  UncontrolledTooltip,
} from "reactstrap"

import { Link } from "react-router-dom"
import { CanView } from "components/Common/CanView"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import InputField from "components/Common/InputField"
import PageWrapper from "components/Common/PageWrapper"
import { TableLocal } from "components/Common/TableLocal"
import { AdvancedTablePagination } from "components/Common/TableContainer"
import { ACCESS } from "constants/accessPermissions"
import {
  useAttendances,
  useDailyAttendanceSummary,
  useDeleteAttendance,
} from "hooks/attendanceHook"
import { useListParams } from "hooks/useListParams"
import {
  formatMinutes,
  formatReadableDateInZone,
  formatReadableTimeInZone,
} from "utils/formatDate"

import AttendanceDetailsModal from "./AttendanceDetailsModal"
import AttendanceManualModal from "./AttendanceManualModal"
import AttendanceSummaryModal from "./AttendanceSummaryModal"

const todayStr = () => new Date().toISOString().slice(0, 10)

const shiftDay = (dateStr, delta) => {
  const d = new Date(dateStr)
  d.setDate(d.getDate() + delta)
  return d.toISOString().slice(0, 10)
}

const STATUS_COLORS = { present: "success", late: "danger" }

const AttendanceList = () => {
  document.title = "Attendance"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selected, setSelected] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [viewModal, setViewModal] = useState(false)
  const [summaryModal, setSummaryModal] = useState({ open: false, filter: null, label: "" })

  const { params, setPage, setSearch, updateParams } = useListParams({
    page: 1,
    search: "",
    date_from: todayStr(),
    date_to: todayStr(),
  })

  const queryParams = useMemo(
    () => ({
      page: params.page ?? 1,
      per_page: 15,
      ...(params.search ? { search: params.search } : {}),
      ...(params.date_from ? { date_from: params.date_from } : {}),
      ...(params.date_to ? { date_to: params.date_to } : {}),
      ...(params.status ? { status: params.status } : {}),
    }),
    [params]
  )

  const {
    data: response,
    isLoading,
    isRefetching,
    refetch,
  } = useAttendances(queryParams)

  const isSingleDay =
    params.date_from && params.date_from === params.date_to
  const { data: summaryResponse } = useDailyAttendanceSummary(
    { date: params.date_from },
    !!isSingleDay
  )
  const summary = summaryResponse?.data

  const items = response?.data?.data ?? []
  const paginationInfo = response?.data ?? null
  const timeZone = response?.data?.attendance_timezone

  const { mutateAsync: removeAttendance, isPending: deleteLoading } =
    useDeleteAttendance()

  const toggleModal = useCallback(() => {
    if (modal) {
      setModal(false)
      setSelected(null)
      setIsEdit(false)
    } else {
      setModal(true)
    }
  }, [modal])

  const handleAddNew = useCallback(() => {
    setSelected(null)
    setIsEdit(false)
    setModal(true)
  }, [])

  const handleEdit = useCallback(item => {
    setSelected(item)
    setIsEdit(true)
    setModal(true)
  }, [])

  const handleDeleteClick = useCallback(item => {
    setSelected(item)
    setDeleteModal(true)
  }, [])

  const handleView = useCallback(item => {
    setSelected(item)
    setViewModal(true)
  }, [])

  const closeView = useCallback(() => {
    setViewModal(false)
    setSelected(null)
  }, [])

  const handleDelete = useCallback(async () => {
    if (!selected?.id) return
    try {
      const res = await removeAttendance(selected.id)
      toast.success(res?.message ?? "Attendance deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch (error) {
      console.error(error)
      toast.error(
        error?.response?.data?.message ?? "Failed to delete attendance."
      )
    }
  }, [selected, removeAttendance, refetch])

  const handlePrevDay = useCallback(() => {
    const from = params.date_from || todayStr()
    const to = params.date_to || todayStr()
    updateParams({ date_from: shiftDay(from, -1), date_to: shiftDay(to, -1), page: 1 })
  }, [params.date_from, params.date_to, updateParams])

  const handleNextDay = useCallback(() => {
    const from = params.date_from || todayStr()
    const to = params.date_to || todayStr()
    updateParams({ date_from: shiftDay(from, 1), date_to: shiftDay(to, 1), page: 1 })
  }, [params.date_from, params.date_to, updateParams])

  const columns = useMemo(
    () => [
      {
        header: "Employee",
        cell: item => {
          const emp = item.employee
          const status = item.status
          const statusLabel = status === "late" ? "Late" : status === "present" ? "On time" : "—"
          const late = item.late_minutes
          const early = item.early_leave_minutes
          const inSrc = item.check_in_source
          const outSrc = item.check_out_source
          const ins = item.check_in_distance_m
          const out = item.check_out_distance_m
          return (
            <div>
              {emp?.id ? (
                <div className="fw-bold text-primary mb-1">
                  <Link to={`/employee-list/${emp.id}/view`}>
                    {emp.name || "—"}
                  </Link>
                </div>
              ) : (
                <div className="fw-bold mb-1">{emp?.name || "—"}</div>
              )}
              <div className="small text-muted">{emp?.employee_id || "—"}</div>
              <div className="small text-muted">
                {formatReadableDateInZone(item.date, timeZone, "EEE, MMM d")}
              </div>

              {/* mobile-only: attendance + status info */}
              <div className="d-md-none mt-2">
                <div className="d-flex align-items-center gap-1 mb-1">
                  <span className="fw-semibold small">
                    {formatReadableTimeInZone(item.check_in_at, timeZone) || "—"}
                  </span>
                  <i className="mdi mdi-arrow-right text-muted" />
                  <span className="fw-semibold small">
                    {formatReadableTimeInZone(item.check_out_at, timeZone) || "—"}
                  </span>
                </div>
                <div className="d-flex flex-wrap gap-1 align-items-center">
                  <Badge color={STATUS_COLORS[status] || "secondary"} className="text-uppercase">
                    {statusLabel}
                  </Badge>
                  {item.is_manual ? (
                    <Badge color="info" className="text-uppercase">Manual</Badge>
                  ) : (
                    <>
                      {inSrc ? (
                        <Badge color="success" className="bg-opacity-25 text-body text-uppercase">
                          In: {inSrc}
                        </Badge>
                      ) : null}
                      {outSrc ? (
                        <Badge color="danger" className="bg-opacity-25 text-body text-uppercase">
                          Out: {outSrc}
                        </Badge>
                      ) : null}
                    </>
                  )}
                </div>
                {late ? <div className="small text-danger mt-1">Late {formatMinutes(late)}</div> : null}
                {early ? <div className="small text-warning">Early {formatMinutes(early)}</div> : null}
                {(ins != null || out != null) && (
                  <div className="small text-muted font-monospace mt-1">
                    {ins != null ? <span>{ins}m in</span> : null}
                    {ins != null && out != null ? <span className="mx-1">/</span> : null}
                    {out != null ? <span>{out}m out</span> : null}
                  </div>
                )}
              </div>
            </div>
          )
        },
      },
      {
        header: "Attendance",
        headerProps: { className: "d-none d-md-table-cell" },
        cellProps: { className: "d-none d-md-table-cell" },
        cell: item => {
          const late = item.late_minutes
          const early = item.early_leave_minutes
          return (
            <div>
              <div className="d-flex align-items-center gap-1 mb-1">
                <span className="fw-semibold">
                  {formatReadableTimeInZone(item.check_in_at, timeZone) || "—"}
                </span>
                <i className="mdi mdi-arrow-right text-muted" />
                <span className="fw-semibold">
                  {formatReadableTimeInZone(item.check_out_at, timeZone) || "—"}
                </span>
              </div>
              {late ? (
                <div className="small text-danger">Late {formatMinutes(late)}</div>
              ) : null}
              {early ? (
                <div className="small text-warning">Early {formatMinutes(early)}</div>
              ) : null}
            </div>
          )
        },
      },
      {
        header: "Status / Source",
        headerProps: { className: "d-none d-md-table-cell" },
        cellProps: { className: "d-none d-md-table-cell" },
        cell: item => {
          const status = item.status
          const label = status === "late" ? "Late" : status === "present" ? "On time" : "—"
          const inSrc = item.check_in_source
          const outSrc = item.check_out_source
          const ins = item.check_in_distance_m
          const out = item.check_out_distance_m
          return (
            <div>
              <div className="mb-1">
                <Badge color={STATUS_COLORS[status] || "secondary"} className="text-uppercase">
                  {label}
                </Badge>
              </div>
              {item.is_manual ? (
                <Badge color="info" className="text-uppercase mb-1">Manual</Badge>
              ) : (
                <div className="d-flex flex-column gap-1 align-items-start mb-1">
                  {inSrc ? (
                    <Badge color="success" className="bg-opacity-25 text-body text-uppercase">
                      In: {inSrc}
                    </Badge>
                  ) : null}
                  {outSrc ? (
                    <Badge color="danger" className="bg-opacity-25 text-body text-uppercase">
                      Out: {outSrc}
                    </Badge>
                  ) : null}
                </div>
              )}
              {(ins != null || out != null) && (
                <div className="small text-muted font-monospace">
                  {ins != null ? <span>{ins}m in</span> : null}
                  {ins != null && out != null ? <span className="mx-1">/</span> : null}
                  {out != null ? <span>{out}m out</span> : null}
                </div>
              )}
            </div>
          )
        },
      },
      {
        header: "Action",
        cell: item => (
          <ul className="list-unstyled d-flex flex-wrap gap-1 mb-0">
            <li>
              <Button
                color="primary"
                size="sm"
                onClick={() => handleView(item)}
                id={`view-att-${item.id}`}
              >
                <i className="mdi mdi-eye-outline" />
              </Button>
              <UncontrolledTooltip target={`view-att-${item.id}`}>
                View details
              </UncontrolledTooltip>
            </li>
            <CanView access={[ACCESS.ATTENDANCE.EDIT]}>
              <li>
                <Button
                  color="info"
                  size="sm"
                  onClick={() => handleEdit(item)}
                  id={`edit-att-${item.id}`}
                >
                  <i className="mdi mdi-pencil-outline" />
                </Button>
                <UncontrolledTooltip target={`edit-att-${item.id}`}>
                  Correct
                </UncontrolledTooltip>
              </li>
            </CanView>
            <CanView access={[ACCESS.ATTENDANCE.DELETE]}>
              <li>
                <Button
                  color="danger"
                  size="sm"
                  onClick={() => handleDeleteClick(item)}
                  id={`del-att-${item.id}`}
                >
                  <i className="mdi mdi-delete-outline" />
                </Button>
                <UncontrolledTooltip target={`del-att-${item.id}`}>
                  Delete
                </UncontrolledTooltip>
              </li>
            </CanView>
          </ul>
        ),
      },
    ],
    [handleView, handleEdit, handleDeleteClick, timeZone]
  )

  return (
    <PageWrapper title="Attendance" breadcrumbItem="Attendance" cardBodyClassName="p-0 p-sm-3">
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />

      {summary && (
        <Row className="mb-3">
          {[
            { label: "Employees", value: summary.total_active_employees, filter: null },
            { label: "Present", value: summary.present, color: "text-success", filter: "present" },
            { label: "Late", value: summary.late, color: "text-danger", filter: "late" },
            { label: "Absent", value: summary.absent, color: "text-warning", filter: "absent" },
            { label: "Checked Out", value: summary.checked_out, filter: "checked_out" },
          ].map(card => (
            <Col key={card.label}>
              <Card
                className="mini-stats-wid mb-0"
                style={card.filter ? { cursor: "pointer" } : undefined}
                onClick={
                  card.filter
                    ? () => setSummaryModal({ open: true, filter: card.filter, label: card.label })
                    : undefined
                }
              >
                <CardBody className="py-3">
                  <p className="text-muted fw-medium mb-1">{card.label}</p>
                  <h5 className={`mb-0 ${card.color || ""}`}>{card.value}</h5>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Row className="mb-3 align-items-center">
        <Col sm={6}>
          <h5 className="mb-0">Attendance Records</h5>
        </Col>
        <Col sm={6} className="text-end">
          <CanView access={[ACCESS.ATTENDANCE.ADD]}>
            <Button color="primary" onClick={handleAddNew} className="me-1">
              <i className="bx bx-plus" /> Manual Entry
            </Button>
          </CanView>
          <Button color="light" onClick={() => refetch()} id="refresh-att">
            <i
              className={`mdi mdi-refresh ${isRefetching ? "mdi-spin" : ""}`}
            />
          </Button>
          <UncontrolledTooltip target="refresh-att">Refresh</UncontrolledTooltip>
        </Col>
      </Row>

      <Row className="mb-3 align-items-center g-2">
        <Col sm={3}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
        <Col sm="auto">
          <Button color="light" size="sm" onClick={handlePrevDay} id="att-prev-day">
            <i className="mdi mdi-chevron-left" />
            <span className="d-inline d-sm-none ms-1">Prev</span>
          </Button>
          <UncontrolledTooltip target="att-prev-day">Previous day</UncontrolledTooltip>
        </Col>
        <Col sm={2}>
          <InputField
            type="date"
            name="date_from"
            value={params.date_from || ""}
            onChange={e =>
              updateParams({ date_from: e.target.value, page: 1 })
            }
          />
        </Col>
        <Col sm={2}>
          <InputField
            type="date"
            name="date_to"
            value={params.date_to || ""}
            onChange={e => updateParams({ date_to: e.target.value, page: 1 })}
          />
        </Col>
        <Col sm="auto">
          <Button color="light" size="sm" onClick={handleNextDay} id="att-next-day">
            <span className="d-inline d-sm-none me-1">Next</span>
            <i className="mdi mdi-chevron-right" />
          </Button>
          <UncontrolledTooltip target="att-next-day">Next day</UncontrolledTooltip>
        </Col>
        <Col sm={2}>
          <InputField
            type="select"
            name="status"
            value={params.status || ""}
            onChange={e => updateParams({ status: e.target.value, page: 1 })}
          >
            <option value="">All Status</option>
            <option value="present">Present</option>
            <option value="late">Late</option>
          </InputField>
        </Col>
      </Row>

      <TableLocal
        columns={columns}
        data={items}
        itemsPerPage={items.length || 15}
        currentPage={1}
        loading={isLoading || isRefetching}
        tableClass="align-middle table-hover mb-0"
        getRowKey={row => row.id}
        emptyMessage={
          <div className="text-center py-5">
            <i className="bx bx-time-five bx-3x text-muted mb-3"></i>
            <h5 className="text-muted">No attendance records found</h5>
            <p className="text-muted mb-0">Try adjusting the date range or filters</p>
          </div>
        }
      />

      {paginationInfo && paginationInfo.total > 0 && (
        <div className="px-3 py-3 border-top">
          <AdvancedTablePagination
            paginationInfo={paginationInfo}
            onPageChange={setPage}
          />
        </div>
      )}

      <AttendanceManualModal
        modal={modal}
        toggleModal={toggleModal}
        isEdit={isEdit}
        editedData={selected}
        timeZone={timeZone}
      />

      <AttendanceDetailsModal
        isOpen={viewModal}
        toggle={closeView}
        record={selected}
        timeZone={timeZone}
      />

      <AttendanceSummaryModal
        isOpen={summaryModal.open}
        toggle={() => setSummaryModal({ open: false, filter: null, label: "" })}
        filter={summaryModal.filter}
        label={summaryModal.label}
        date={params.date_from}
        timeZone={timeZone}
      />
    </PageWrapper>
  )
}

export default AttendanceList
